import React, { useState, useEffect, useRef } from "react";
import Config from "../../Config";
import PropTypes from "prop-types";
import {
  CognitoUserPool,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { connect } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";

import RegistrationForm from "./components/RegistrationForm/RegistrationForm";

import { TAXNUMBER_STATUS } from "LixaniAPI/enums";
import {
  setEmail,
  setPhone,
  setIsPhoneAccount,
} from "../../store/actions/user";
import { setRoute, ROUTE } from "../../store/actions/router";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { passwordHelper } from "util/password";
import { password2Helper } from "util/password2";
import { setUserRegistrationActivity } from "services/set-user-registration-activity";
import { debounce } from "lodash";
import { useMemo } from "react";
import { fetchFromFinder } from "util/fetchFromFinder";
import { fetchBusinessInfo } from "util/fetchBusinessInfo";
import { parseCompanyData, parseStatus } from "util/helpers";
import { checkTaxNumber } from "services/check-tax-number";
import { phoneNormalization } from "util/phoneNormalization";
import { avatarUserSearch } from "util/avatarUserSearch";
import { fetchBusinessInfoBySearchTerm } from "util/fetchBusinessInfoBySearchTerm";

const initialDateOfBirth = () => {
  let d = new Date();
  const currentYear = d.getFullYear();
  d.setFullYear(currentYear - 14);
  d.setMonth(0);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const maxDateOfBirth = () => {
  let d = new Date();
  const currentYear = d.getFullYear();
  d.setFullYear(currentYear - 13);
  d.setMonth(-1);
  d.setDate(-1);
  return d.toISOString().slice(0, 10);
};

const minDateOfBirth = () => {
  let d = new Date();
  const currentYear = d.getFullYear();
  d.setFullYear(currentYear - 120);
  return d.toISOString().slice(0, 10);
};

const initRegistrationData = {
  business_id: { value: "", required: true, error: false, type: "business_id" },
  city: { value: "", required: false, error: false },
  co: { value: "", required: false, error: false },
  contact_first_name: { value: "", required: false, error: false },
  contact_last_name: { value: "", required: false, error: false },
  contact_street1: { value: "", required: false, error: false },
  contact_street2: { value: "", required: false, error: false },
  contact_co: { value: "", required: false, error: false },
  contact_postcode: { value: "", required: false, error: false },
  contact_city: { value: "", required: false, error: false },
  contact_country: { value: "", required: false, error: false },
  contact_email: { value: "", required: false, error: false },
  contact_phone: { value: "", required: false, error: false },
  country: { value: "", required: false, error: false },
  dateOfBirth: {
    value: "",
    required: true,
    error: false,
    errorType: "",
    max: maxDateOfBirth(),
    min: minDateOfBirth(),
    type: "date",
  },
  dateOfBirthDay: {
    value: "",
    required: true,
    error: false,
    min: 1,
    max: 31,
  },
  dateOfBirthMonth: {
    value: "",
    required: true,
    error: false,
    min: 1,
    max: 12,
  },
  dateOfBirthYear: {
    value: "",
    required: true,
    error: false,
    min: new Date().getFullYear() - 120,
    max: new Date().getFullYear() - 13,
  },
  email: { value: "", required: false, error: false, type: "email" },
  explanation: { value: "", required: false, error: false },
  first_name: { value: "", required: true, error: false },
  last_name: { value: "", required: true, error: false },
  middle_name: { value: "", required: false, error: false },
  name: { value: "", required: true, error: false },
  password: {
    value: "",
    required: true,
    error: false,
    type: "password",
    check_same: "password2",
  },
  password2: {
    value: "",
    required: true,
    error: false,
    type: "password",
    is_same: "password",
  },
  phone: { value: "", required: true, error: false, type: "phone" },
  postcode: { value: "", required: false, error: false },
  prefill: { value: false, required: false, error: false },
  projectKey: { value: "", required: true, error: false, disabled: false },
  street1: { value: "", required: false, error: false },
  street2: { value: "", required: false, error: false },
  taxNumber: { value: "", required: true, error: false },
  employmentRelationship: {
    value: "",
    required: true,
    error: false,
  },
};

function RegistrationLogic(props) {
  const { t, i18n } = useTranslation("common");
  const [cognitoUser, setCognitoUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stateError, setError] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    ...initRegistrationData,
  });
  const [taxNumberCheckedAt, setTaxNumberCheckedAt] = useState("");
  const [taxNumberStatus, setTaxNumberStatus] = useState(
    TAXNUMBER_STATUS.UNCHECKED,
  );
  const [userConfirmed, setUserConfirmed] = useState(false);
  const [tradeRegisterStatus, setTradeRegisterStatus] = useState("no");
  const [businessIdCountry, setBusinessIdCountry] = useState("finland");
  const [businessIdStatus, setBusinessIdStatus] = useState(false);
  const [foundByBusinessId, setFoundByBusinessId] = useState(false);
  const [validCompany, setValidCompany] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [dataFoundByTaxnumberCheck, setDataFoundByTaxNumberCheck] = useState(false);
  const [companySearchValue, setCompanySearchValue] = useState(null);
  const [companySearchOptions, setCompanySearchOptions] = useState([]);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);

  const [activityId] = useState(uuidv4());
  const hasUserInteraction = useRef(false);
  const lastSavedActivity = useRef(null);

  const [taxNumberLoading, setTaxNumberLoading] = useState(false);

  const registrationActivity = useMemo(() => {
    return {
      id: activityId,
      phone: registrationData.phone.value,
      projectKey: registrationData.projectKey.value,
      first_name: registrationData.first_name.value,
      last_name: registrationData.last_name.value,
      dateOfBirth: registrationData.dateOfBirth.value,
      taxNumber: registrationData.taxNumber.value,
      tradeRegisterStatus: tradeRegisterStatus,
      businessIdStatus: businessIdStatus,
      businessIdCountry: businessIdCountry,
      business_id: registrationData.business_id.value,
      name: registrationData.name.value,
      status: "INCOMPLETE",
      type: "EMPLOYEE",
      employmentRelationship: registrationData.employmentRelationship.value,
    };
  }, [
    registrationData,
    tradeRegisterStatus,
    businessIdStatus,
    businessIdCountry,
  ]);

  const mergeActivity = (prev, next) => {
    if (!prev) return next;
    const keep = (field) => (next[field] ? next[field] : prev[field]);
    return {
      ...next,
      first_name: keep("first_name"),
      last_name: keep("last_name"),
      dateOfBirth: keep("dateOfBirth"),
      taxNumber: keep("taxNumber"),
      business_id: keep("business_id"),
      name: keep("name"),
      employmentRelationship: keep("employmentRelationship"),
    };
  };

  // Debounced PUT
  const debouncedSaveActivity = useRef(
    debounce(async (data) => {
      await setUserRegistrationActivity(data);
    }, 500),
  ).current;

  /**
   * Handle saving user registration activity
   * @param {object} partialActivity
   * @returns
   */
  const saveActivity = (partial = {}) => {
    hasUserInteraction.current = true;
    const base = { ...registrationActivity, ...partial };
    const merged = mergeActivity(lastSavedActivity.current, base);
    lastSavedActivity.current = merged;
    debouncedSaveActivity(merged);
  };

  const handlePhoneValidationBlur = async (phonenumber) => {
    const validatedPhone = phoneNormalization(phonenumber);
    props.onSetPhone(validatedPhone);

    const avatarUsers = await avatarUserSearch("phone", validatedPhone, 8);

    if (avatarUsers.length === 1) {
      const avatarUser = avatarUsers[0];
      const dateOfBirth = avatarUser.dateOfBirth
        ? moment(avatarUser.dateOfBirth).format("YYYY-MM-DD")
        : "";

      const dateOfBirthArray = dateOfBirth.split("-");

      // Save enriched data immediately (prevent overwrite)
      saveActivity({
        phone: validatedPhone,
        first_name: avatarUser.firstName || "",
        last_name: avatarUser.lastName || "",
        taxNumber: avatarUser.taxNumber || "",
        dateOfBirth: dateOfBirth,
        business_id: avatarUser.employerBusinessId || "",
      });

      setRegistrationData((prevState) => ({
        ...prevState,
        dateOfBirth: {
          ...prevState.dateOfBirth,
          value: dateOfBirth,
        },
        dateOfBirthDay: {
          ...prevState.dateOfBirthDay,
          value: parseInt(dateOfBirthArray[2], 10),
        },
        dateOfBirthMonth: {
          ...prevState.dateOfBirthMonth,
          value: parseInt(dateOfBirthArray[1], 10),
        },
        dateOfBirthYear: {
          ...prevState.dateOfBirthYear,
          value: dateOfBirthArray[0],
        },
        taxNumber: {
          ...prevState.taxNumber,
          value: avatarUser.taxNumber,
        },
        first_name: {
          ...prevState.first_name,
          value: avatarUser.firstName,
        },
        last_name: {
          ...prevState.last_name,
          value: avatarUser.lastName,
        },
        phone: {
          ...prevState.phone,
          value: validatedPhone,
        },
        business_id: {
          ...prevState.business_id,
          value: avatarUser.employerBusinessId || "",
        },
      }));

      if (avatarUser.employerBusinessId) {
        handleChangeBusinessId({
          target: {
            value: avatarUser.employerBusinessId || "",
          },
        });
      }

      if (avatarUser.dateOfBirth && avatarUser.taxNumber) {
        await handleTaxNumberCheck({
          taxNumber: avatarUser.taxNumber,
          dateOfBirth: avatarUser.dateOfBirth,
        });
      }
    } else {
      setRegistrationData((prevState) => ({
        ...prevState,
        phone: {
          ...prevState.phone,
          value: validatedPhone,
        },
      }));
      saveActivity({ phone: validatedPhone });
    }
  };

  useEffect(() => {
    const initPhone = () => {
      let prefill = "";

      // callback if not signed in.
      try {
        prefill = decodeURIComponent(
          new URL(window.location.href).search.split("?callback="),
        ).split("?data=");
      } catch (err) {
        prefill = new URL(window.location.href).search.split("?data=");
      }

      let prefilldata = {};
      if (prefill.length > 1) {
        try {
          let d = JSON.parse(atob(prefill[1]));
          prefilldata = Object.keys(d).reduce((resp, key) => {
            resp[key] = { ...initRegistrationData[key], value: d[key] };
            return resp;
          }, {});
          prefilldata["prefill"] = {
            value: d.prefill ? d.prefill.value : true,
            required: false,
            error: false,
          };
        } catch (err) {
          console.log(err);
        }
      }

      if (!props.phone && prefilldata["phone"]) {
        props.onSetPhone(prefilldata["phone"].value);
      }

      return prefilldata["phone"] || "";
    };

    const initProjectKey = () => {
      let projectKey = "";
      let prefill = "";
      let joinUrl = "";

      // callback if not signed in.
      try {
        prefill = decodeURIComponent(
          new URL(window.location.href).search.split("?callback="),
        ).split("?data=");
      } catch (err) {
        prefill = new URL(window.location.href).search.split("?data=");
      }

      try {
        const url = new URL(window.location.href).search.split("?callback=");
        if (url && url.length > 1 && url[1]) {
          console.log("Project join URL detected");
          joinUrl = decodeURIComponent(url[1]);
          if (joinUrl && joinUrl.includes("/project/join/")) {
            const urlParts = joinUrl.split("/project/join/");
            if (urlParts.length > 1) {
              const projectKey = urlParts[1].split("/")[0];

              // Check if projectKey is valid
              if (projectKey && projectKey.trim() !== "") {
                setRegistrationData((prevState) => ({
                  ...prevState,
                  projectKey: {
                    ...prevState.projectKey,
                    value: projectKey,
                    disabled: true,
                  },
                }));
              } else {
                console.log("Invalid or empty project key extracted");
              }
            }
          }
        }
      } catch (error) {
        console.log("Error parsing URL:", error.message);
      }

      if (prefill.length > 1) {
        try {
          let d = JSON.parse(atob(prefill[1]));
          setRegistrationData((prevState) => {
            return {
              ...prevState,
              projectKey: {
                ...prevState.projectKey,
                value: d.project ? d.project.projectKey : "",
                disabled: true,
              },
            };
          });
        } catch (err) {
          console.log(err);
        }
      }

      return projectKey;
    };

    initPhone();
    initProjectKey();
  }, []);

  const register = async () => {
    setError(null);
    setLoading(true);

    hasUserInteraction.current = true;
    saveActivity();

    let userPool = new CognitoUserPool({
      UserPoolId: Config.COGNITO_USER_POOL_ID,
      ClientId: Config.COGNITO_CLIENT_ID,
    });

    const data = { ...registrationData };
    const email = props.email.toLowerCase();
    const phone = props.phone.replace(/[^a-zA-Z0-9+ ]/g, "");
    //data.phone.value; //props.phone;
    const username = props.isPhoneAccount ? phone : email;

    let attributeList = [];
    attributeList.push(
      new CognitoUserAttribute({ Name: "email", Value: email }),
    );
    attributeList.push(
      new CognitoUserAttribute({
        Name: "phone_number",
        Value: data.phone.value.replace(/[^a-zA-Z0-9+ ]/g, ""),
      }),
    );
    attributeList.push(
      new CognitoUserAttribute({
        Name: "given_name",
        Value: data.first_name.value,
      }),
    );
    attributeList.push(
      new CognitoUserAttribute({
        Name: "family_name",
        Value: data.last_name.value,
      }),
    );

    //    if (data.middle_name.value) {
    //      attributeList.push(new CognitoUserAttribute({ Name: 'middle_name', Value: data.middle_name.value }));
    //    }

    if (data.projectKey.value && data.projectKey.value.length > 2) {
      addProjectKeyToUrl();
    }

    const reddata = Object.keys(data)
      .filter(
        (key) =>
          ![
            "project",
            "password",
            "password2",
            "email",
            "phone",
            "first_name",
            "last_name",
            "middle_name",
            "projectKey",
            "dateOfBirthDay",
            "dateOfBirthMonth",
            "dateOfBirthYear",
          ].includes(key),
      )
      .reduce((resp, key) => {
        const ob = data[key];
        if (ob.value) {
          resp[key] = ob.value;
        }
        return resp;
        //			({[key]:data[key].value})
      }, {});

    //-- Add taxNumber check date and status
    if (taxNumberCheckedAt) {
      reddata["taxNumberCheckedAt"] = taxNumberCheckedAt;
      reddata["taxNumberStatus"] = taxNumberStatus;
    }

    attributeList.push(
      new CognitoUserAttribute({
        Name: "custom:employee_base64",
        Value: btoa(JSON.stringify(reddata)),
      }),
    );

    //-- Add locale attribute
    let locale = i18n.language;
    if (!locale) {
      locale = navigator.language || navigator.userLanguage;
    }
    locale = locale.split("-")[0];
    attributeList.push(
      new CognitoUserAttribute({ Name: "locale", Value: locale }),
    );

    let result = null;
    try {
      result = await new Promise((resolve, reject) => {
        userPool.signUp(
          username,
          data.password.value,
          attributeList,
          null,
          (error, result) => {
            if (error) reject(error);
            else resolve(result);
          },
        );
      });
    } catch (error) {
      setLoading(false);
      console.error("Registration error", JSON.stringify(error));
      if (error.code === "UsernameExistsException") {
        setError(props.t("error.already_registered"));
      } else if (error.message === "Invalid phone number format.") {
        setError(props.t("error.invalid_phone_format"));
      } else {
        setError(error.message);
      }
      return;
    }

    setCognitoUser(result.user);
    setRegistered(true);
    setRegistrationData({ ...initRegistrationData });
    setUserConfirmed(result.userConfirmed);
  };

  const addProjectKeyToUrl = () => {
    const currentUrl = new URL(window.location.href);
    const hasDataParam = currentUrl.search.includes("?data=");

    if (!hasDataParam) {
      const dataToEncode = {
        project: {
          projectKey: registrationData.projectKey.value,
        },
      };

      // Encode the data
      const encodedData = btoa(JSON.stringify(dataToEncode));

      // Add to URL
      // No parameters yet, add data parameter
      currentUrl.search = `?data=${encodedData}`;
    }

    // Update the browser URL without reloading the page
    window.history.pushState({}, "", currentUrl.toString());
    console.log("Added projectKey to URL:", currentUrl.toString());
  };

  const handleDateOfBirthYearOnBlur = (name) => (event) => {
    if (name === "dateOfBirthYear") {
      const yearValue = event.target.value;

      if (yearValue && yearValue.length !== 4) {
        setRegistrationData((prevState) => ({
          ...prevState,
          dateOfBirthYear: {
            ...prevState.dateOfBirthYear,
            error: true,
          },
          dateOfBirth: {
            ...prevState.dateOfBirth,
            error: true,
          },
        }));
      } else {
        setRegistrationData((prevState) => ({
          ...prevState,
          dateOfBirthYear: {
            ...prevState.dateOfBirthYear,
            error: false,
          },
          dateOfBirth: {
            ...prevState.dateOfBirth,
            error: false,
          },
        }));
      }
    }
  };

  // Debounced company search (by name or business id) against the backend.
  const debouncedCompanySearch = useRef(
    debounce(async (term) => {
      const trimmed = (term || "").trim();
      if (trimmed.length < 2) {
        setCompanySearchOptions([]);
        setCompanySearchLoading(false);
        return;
      }
      try {
        const data = await fetchBusinessInfoBySearchTerm(trimmed);
        setCompanySearchOptions(Array.isArray(data) ? data : []);
      } finally {
        setCompanySearchLoading(false);
      }
    }, 400),
  ).current;

  const handleSearchTermChange = (newValue) => {
    setCompanySearchValue(newValue);
    const trimmed = (newValue || "").trim();
    // When the field is emptied (or too short to search), clear results
    // immediately and cancel any pending search so stale results don't linger.
    if (trimmed.length < 2) {
      debouncedCompanySearch.cancel();
      setCompanySearchOptions([]);
      setCompanySearchLoading(false);
      return;
    }
    // Show the spinner as soon as a searchable term is typed; the debounced
    // search clears it once results arrive.
    setCompanySearchLoading(true);
    debouncedCompanySearch(newValue);
  };

  /**
   * User picked a company from the search results: populate the employer
   * fields by reusing the existing business id lookup flow.
   * @param {object} selectedOption - { name, business_id }
   */
  const handleCompanySelect = (selectedOption) => {
    if (!selectedOption) return;
    setCompanySearchValue(
      `${selectedOption.name} (${selectedOption.business_id})`,
    );
    if (selectedOption.business_id) {
      handleChangeBusinessId({
        target: { value: selectedOption.business_id },
      });
    }
  };

  // An employee of a limited company (osakeyhtiö) cannot be an independent
  // practitioner, so that employment relationship option is not allowed.
  const isEmployerLimitedCompany = (companyData?.businessForm || "")
    .toLowerCase()
    .includes("osakeyhtiö");

  useEffect(() => {
    // Clear a now-invalid selection if the resolved employer is a limited company.
    if (
      isEmployerLimitedCompany &&
      registrationData.employmentRelationship.value === "practitioner"
    ) {
      setRegistrationData((prevState) => ({
        ...prevState,
        employmentRelationship: {
          ...prevState.employmentRelationship,
          value: "",
        },
      }));
    }
  }, [isEmployerLimitedCompany, registrationData.employmentRelationship.value]);

  const handleChangeData = (name) => (event) => {
    let data = { ...registrationData[name] };
    let value = event;
    try {
      value = event.target.value;
    } catch (err) {
      //console.log(err);
    }

    // User interaction has happened
    hasUserInteraction.current = true;

    if (name === "phone") {
      value = value.replace(/ /g, "");
    }
    data.value = value;

    if (data.type === "email") {
      props.onSetEmail(value); //event.target.value);
    } else if (data.type === "phone") {
      props.onSetPhone(value); //event.target.value);
      //props.onSetPhone(event);
    }

    if (name === "taxNumber") {
      value = value.replace(/[^0-9]/g, "");
      data.value = value;
    }

    if (
      name === "dateOfBirthDay" ||
      name === "dateOfBirthMonth" ||
      name === "dateOfBirthYear"
    ) {
      // Use functional state update to ensure we have the latest state
      setRegistrationData((prevState) => {
        // Create updated copy of the specific field
        const updatedField = { ...prevState[name], value: value };

        // Get the most current values using prevState
        const day =
          name === "dateOfBirthDay" ? value : prevState.dateOfBirthDay.value;
        const month =
          name === "dateOfBirthMonth"
            ? value
            : prevState.dateOfBirthMonth.value;
        const year =
          name === "dateOfBirthYear" ? value : prevState.dateOfBirthYear.value;

        // Create a copy of the entire state with the updated field
        const updatedState = { ...prevState, [name]: updatedField };

        // Only update the combined date if all components have values
        if (day && month && year) {
          const paddedDay = day.toString().padStart(2, "0");
          const paddedMonth = month.toString().padStart(2, "0");
          const dateString = `${year}-${paddedMonth}-${paddedDay}`;

          console.log(" dateString:", dateString);
          // Validate the date
          let dateError = false;
          const dateValue = moment(dateString);
          console.log(" dateValue:", dateValue);

          if (!dateValue.isValid()) {
            dateError = true;
            const maxDaysInMonth = moment(
              `${year}-${paddedMonth}`,
            ).daysInMonth();

            if (parseInt(day) > maxDaysInMonth) {
              updatedState.dateOfBirthDay = {
                ...updatedState.dateOfBirthDay,
                error: true,
              };
            }
          } else {
            dateError = false;
            updatedState.dateOfBirthDay = {
              ...updatedState.dateOfBirthDay,
              error: false,
            };
            if (prevState.taxNumber.value) {
              handleTaxNumberCheck({
                taxNumber: prevState.taxNumber.value,
                dateOfBirth: dateString,
              });
            }
            saveActivity({ dateOfBirth: dateString });
          }

          updatedState.dateOfBirth = {
            ...prevState.dateOfBirth,
            value: dateString,
            error: dateError,
          };
        }

        return updatedState;
      });

      return;
    }

    data.error = !dataValid(data)(true);

    setRegistrationData((prevState) => ({
      ...prevState,
      [name]: data,
    }));
  };

  const dataValid = (data) => (check) => {
    let value = data.value;

    if (
      data.type === "date" ||
      ["dateOfBirthDay", "dateOfBirthMonth", "dateOfBirthYear"].includes(
        data.name,
      )
    ) {
      if (data.error) {
        return false;
      }
      return true;
    }

    if (data.type === "business_id" && data.error === true) {
      return false;
    }

    const isEmpty = typeof value !== "string" ? !value : value.trim() === "";

    if (data.required && isEmpty) {
      return false;
    }

    if (data.is_same) {
      if (
        password2Helper(props.t)(
          registrationData[data.is_same].value,
          value,
        ) !== null
      )
        return false;
    } else {
      if (data.type === "password" && passwordHelper(props.t)(value) !== null) {
        return false;
      } else if (check && data.type === "business_id") {
        console.log("BUSINESS ID CHECK");
        // check y-tunnus
        let ytunnus = "0000000" + data.value.match(/\d/g).join("");
        ytunnus = ytunnus.substr(ytunnus.length - 8, 8).split("");
        let cr1 = parseInt(ytunnus.pop());

        const c = ytunnus.reduce(
          (resp, ch) => {
            const i = parseInt(ch);
            const k = resp.shift();
            resp[resp.length - 1] += i * k;
            return resp;
          },
          [7, 9, 10, 5, 8, 4, 2, 0],
        );
        const cr2 = c % 11 === 0 ? 0 : 11 - (c % 11);

        // y-tunnus ok. check sv
        if (cr1 === cr2) {
          const url =
            "https://selvitysvelvollisuus.fi:9000/svapi/company-info/";
          //	const url='https://dev.selvitysvelvollisuus.fi:9000/svapi/company-info/';
          fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ business_id: data.value }),
          })
            .then((res) => res.json())
            .then((x) => {
              console.log("löytyi", x);
              const d = { ...registrationData["name"] };
              d.value = x.trade_name;

              setRegistrationData({
                ...registrationData,
                ["name"]: d || "",
              });
            })
            .catch((res) => {
              console.log(res);
            });
        }
      }
    }
    return true;
  };

  const handleBusinessIdCountryRadioChange = (e) => {
    setBusinessIdCountry(e.target.value);
    setFoundByBusinessId(false);
  };

  /**
   * Handle business id change
   * @param {*} event - event or value
   * @returns - void
   */
  const handleChangeBusinessId = (event) => {
    let businessIdData = { ...registrationData["business_id"] };
    let nameData = { ...registrationData["name"] };
    let businessIdValue = event;
    try {
      businessIdValue = event.target.value;

      if (businessIdValue.length === 0) {
        // Clear out any previously found company so the name field is
        // editable again (it is disabled while a valid company is selected).
        setFoundByBusinessId(false);
        setValidCompany(false);
        setCompanyData(null);
        setBusinessIdStatus(false);
        setTradeRegisterStatus("no");
        nameData.value = "";
        nameData.error = false;
        nameData.errorMessage = "";
        businessIdData.value = "";
        businessIdData.error = false;
        businessIdData.errorMessage = "";
        setRegistrationData((prevState) => ({
          ...prevState,
          business_id: { ...businessIdData },
          name: { ...nameData },
        }));
        return;
      }

      if (businessIdCountry === "finland") {
        // Only allow numbers and dashes in businessId
        businessIdValue = businessIdValue.replace(/[^0-9-]/g, "");

        if (businessIdValue.length > 9) {
          businessIdValue = businessIdValue.substring(0, 9);
        }

        // If businessId either 8 numbers or 7 numbers and a dash followed by a number
        if (
          /^\d{7}-\d{1}$/.test(businessIdValue) ||
          /^\d{8}$/.test(businessIdValue)
        ) {
          if (/^\d{8}$/.test(businessIdValue)) {
            // add dash between 7th and 8th digit
            businessIdValue = businessIdValue.replace(/(\d{7})(\d)/, "$1-$2");
          }

          fetchBusinessInfo(businessIdValue).then((data) => {
            const { message, error } = data;
            console.log("fetchBusinessInfo data:", data);
            console.log("message:", message);
            if (
              !data ||
              !data.businessName ||
              message === "Company not found" ||
              error
            ) {
              console.log("business not found from fetchBusinessInfo");
              saveActivity({
                business_id: businessIdValue,
                businessIdStatus: "NOT_FOUND",
                tradeRegisterStatus: "no",
                name: "",
              });
              setFoundByBusinessId(false);
              setValidCompany(false);
              nameData.value = "";
              businessIdData.value = businessIdValue;
              setRegistrationData((prevState) => ({
                ...prevState,
                business_id: { ...businessIdData },
                name: { ...nameData },
              }));
              return;
            }

            setFoundByBusinessId(true);
            const companyData = parseCompanyData(data);
            setCompanyData(companyData);

            const status = parseStatus(data);

            if (status.status === "REJECTED") {
              businessIdData.error = true;
              businessIdData.errorMessage = t(
                `error.business_id.${status.rejectionReason.toLowerCase()}`,
              );
              setBusinessIdStatus(status.rejectionReason);
              setTradeRegisterStatus(status.tradeRegisterStatus || "no");
              setRegistrationData((prevState) => ({
                ...prevState,
                business_id: { ...businessIdData },
                name: { ...nameData },
              }));

              saveActivity({
                business_id: businessIdValue,
                name: "",
                tradeRegisterStatus: status.tradeRegisterStatus || "no",
                businessIdStatus: status.rejectionReason,
              });

              setValidCompany(false);
              return;
            } else {
              setBusinessIdStatus(status.status);
              setTradeRegisterStatus(status.tradeRegisterStatus || "no");
              businessIdData.error = false;
              businessIdData.errorMessage = "";

              nameData.value = data.businessName;
              nameData.error = false;
              setValidCompany(true);

              setRegistrationData((prevState) => ({
                ...prevState,
                business_id: { ...businessIdData },
                name: { ...nameData },
              }));

              saveActivity({
                business_id: businessIdValue,
                name: data.businessName,
                tradeRegisterStatus: status.tradeRegisterStatus || "no",
                businessIdStatus: status.status,
              });
            }
          });

          // NOTE: CAN BE ADDED FOR BACKUP, BUT THERE SHOULD BE NO REASON WHY THE PAID VERSION USED IN
          // fetchBusinessInfo WOULD NOT FIND THE COMPANY and THE FREE VERSION WOULD FIND IT (for example: free version does not provide toiminimiyrittäjät)
          // const url = `https://avoindata.prh.fi/opendata-ytj-api/v3/companies?businessId=${businessIdValue}`;
          // console.log("url", url);
          // //curl -X GET --header 'Accept: application/json' 'https://avoindata.prh.fi/opendata-ytj-api/v3/companies?businessId=${businessIdValue}'
          // // fetch using the above curl command
          // fetch(url, {
          //   headers: {
          //     Accept: "application/json",
          //   },
          // })
          //   .then((res) => res.json())
          //   .then((x) => {
          //     console.log("x", x);
          //     if (x.companies.length > 0 && x.companies[0].names && x.companies[0].names[0].name) {
          //       nameData.value = x.companies[0].names[0].name;
          //       nameData.error = false;

          //       const company = x.companies[0];

          //       setFoundByBusinessId(true);
          //       if (company.status === '2' && company.tradeRegisterStatus === '1') {
          //         setTradeRegisterStatus('yes');
          //         setBusinessIdStatus('ACTIVE');
          //       } else if (company.status === '5') {
          //         setBusinessIdStatus('REMOVED');
          //         businessIdData.error = true;
          //       } else if (company.status === '1') {
          //         setBusinessIdStatus('PENDING');
          //         businessIdData.error = true;
          //       }

          //       setRegistrationData((prevState) => ({
          //         ...prevState,
          //         business_id: { ...businessIdData },
          //         name: { ...nameData },
          //       }));

          //     } else {
          //       // console.log("not found from avoin data, trying finder");
          //       // fetchFromFinder({ businessIdValue }).then((response) => {
          //       //   if (!response || !response.companyName) {
          //       //     console.log("business not found from finder");
          //       //   } else {
          //       //     nameData.value = response.companyName;
          //       //     nameData.error = false;
          //       //     businessIdData.value = businessIdValue;
          //       //     setFoundByBusinessId(true);
          //       //     setTradeRegisterStatus('unknown');
          //       //     setBusinessIdStatus('x');

          //       //     setRegistrationData((prevState) => ({
          //       //       ...prevState,
          //       //       business_id: { ...businessIdData },
          //       //       name: { ...nameData },
          //       //     }));
          //       //   }
          //       // })
          //     }
          //   })
          //   .catch((res) => {
          //     console.log(res);
          //   });
        }
      }
    } catch (err) {
      //console.log(err);
    }

    businessIdData.value = businessIdValue;

    setRegistrationData((prevState) => ({
      ...prevState,
      business_id: { ...businessIdData },
      name: { ...nameData },
    }));

    hasUserInteraction.current = true;
    saveActivity({
      business_id: businessIdData.value,
      name: nameData.value,
    });
  };

  /**
   * Handle tax number check from Vero API
   * @param {object} variables - taxNumber and dateOfBirth
   */
  const handleTaxNumberCheck = async (variables) => {
    let taxNumberError = false;
    let taxNumberChecked = false;
    let taxNumberErrorMessage = "";

    checkTaxNumber(variables)
      .then((res) => {
        if (res.status === 200) {
          const message = res.data;
          if (message.InfoExists === false) {
            taxNumberError = true;
            taxNumberErrorMessage = t("auth.tax_number_error_or_inactive");
          } else if (message.TaxNumberMatchBirthday === false) {
            taxNumberError = true;
            taxNumberErrorMessage = t("auth.tax_number_birthday_mismatch");
          }

          // Auto input first and last name if available
          if (message.FirstName && message.LastName) {
            // Split FirstName to separate first name
            const nameParts = message.FirstName.trim().split(" ");
            const firstName = nameParts[0] || "";

            setRegistrationData((prevState) => ({
              ...prevState,
              first_name: {
                ...prevState.first_name,
                // Only update if field is empty
                value:
                  prevState.first_name.value.trim() === ""
                    ? firstName
                    : prevState.first_name.value,
              },
              last_name: {
                ...prevState.last_name,
                // Only update if field is empty
                value:
                  prevState.last_name.value.trim() === ""
                    ? message.LastName
                    : prevState.last_name.value,
              },
            }));
            saveActivity({
              taxNumber: registrationData.taxNumber.value,
              first_name: firstName,
              last_name: message.LastName,
            });
            setDataFoundByTaxNumberCheck(true);
          }

          taxNumberChecked = true;
        } else if (res.status === 400) {
          // If error is missing birth date, do not set error, means user has not input it yet
          if (res?.data === "Missing birth date") {
            taxNumberErrorMessage = t("auth.tax_number_birthday_mismatch");
          }
          taxNumberError = true;
          taxNumberChecked = true;
          if (res.data.ErrorCode === "1005") {
            taxNumberErrorMessage = t("auth.tax_number_too_short");
          }
        } else if (res.status === 404 || res.status === 500) {
          if (res.data.ErrorCode) {
            console.log(res.data.ErrorCode);
            console.log(res.data.ErrorText);
          }
        }
        if (taxNumberChecked) {
          console.log("taxNumberChecked", taxNumberChecked);
          setTaxNumberCheckedAt(new Date().toISOString());
          setTaxNumberStatus(
            taxNumberError ? TAXNUMBER_STATUS.INVALID : TAXNUMBER_STATUS.VALID,
          );
        }

        setRegistrationData((prevState) => ({
          ...prevState,
          taxNumber: {
            ...registrationData.taxNumber,
            value: variables.taxNumber,
            error: taxNumberError,
            errorMessage: taxNumberErrorMessage,
          },
        }));

        setTaxNumberLoading(false);

        saveActivity({ taxNumber: registrationData.taxNumber.value });
      })
      .catch((error) => {
        // Also set loading to false on error
        setTaxNumberLoading(false);
        console.error("Tax number check error:", error);
      });
  };

  /**
   * Handle tax number input on blur (avatar user search + vero check)
   * @returns - void
   */
  const handleTaxNumberOnBlur = async () => {
    setTaxNumberLoading(true);

    setRegistrationData((prevState) => ({
      ...prevState,
      taxNumber: { ...registrationData.taxNumber, error: false },
    }));

    const dateOfBirth = registrationData.dateOfBirth.value;
    const taxNumber = registrationData.taxNumber.value;
    if (taxNumber.length === 0) {
      setTaxNumberLoading(false);
      return;
    }

    const variables = { taxNumber, dateOfBirth };

    const avatarUsers = await avatarUserSearch("taxNumber", taxNumber, 12);
    if (avatarUsers.length === 1) {
      console.log("Found avatar user by tax number:", avatarUsers[0]);
      const user = avatarUsers[0];
      const dob = user.dateOfBirth
        ? moment(user.dateOfBirth).format("YYYY-MM-DD")
        : "";
      const dobParts = dob.split("-");
      setRegistrationData((prevState) => ({
        ...prevState,
        first_name: {
          ...prevState.first_name,
          value:
            prevState.first_name.value.trim() === ""
              ? user.firstName || prevState.first_name.value
              : prevState.first_name.value,
        },
        last_name: {
          ...prevState.last_name,
          value:
            prevState.last_name.value.trim() === ""
              ? user.lastName || prevState.last_name.value
              : prevState.last_name.value,
        },
        taxNumber: {
          ...prevState.taxNumber,
          value: user.taxNumber || prevState.taxNumber.value,
        },
        dateOfBirth: {
          ...prevState.dateOfBirth,
          value: dob || prevState.dateOfBirth.value,
        },
        dateOfBirthDay: {
          ...prevState.dateOfBirthDay,
          value: dob
            ? parseInt(dobParts[2], 10)
            : prevState.dateOfBirthDay.value,
        },
        dateOfBirthMonth: {
          ...prevState.dateOfBirthMonth,
          value: dob
            ? parseInt(dobParts[1], 10)
            : prevState.dateOfBirthMonth.value,
        },
        dateOfBirthYear: {
          ...prevState.dateOfBirthYear,
          value: dob ? dobParts[0] : prevState.dateOfBirthYear.value,
        },
        business_id: {
          ...prevState.business_id,
          value: user.employerBusinessId || prevState.business_id.value,
        },
      }));

      // Save enriched fields immediately
      saveActivity({
        taxNumber: user.taxNumber || taxNumber,
        first_name: user.firstName || "",
        last_name: user.lastName || "",
        dateOfBirth: dob || dateOfBirth,
        business_id: user.employerBusinessId || "",
      });

      // Trigger business id fetch if available
      if (user.employerBusinessId) {
        handleChangeBusinessId({ target: { value: user.employerBusinessId } });
      }

      const newVariables = { taxNumber, dateOfBirth: dob };

      console.log("handleTaxNumberCheck start (with avatar dob)");
      await handleTaxNumberCheck(newVariables);
      console.log("handleTaxNumberCheck done");
    } else {
      console.log("handleTaxNumberCheck start (no avatar)");
      await handleTaxNumberCheck(variables);
      console.log("handleTaxNumberCheck done");
    }
  };

  const togglePhoneAccount = () => {
    const isPhoneAccount = !props.isPhoneAccount;
    setRegistrationData((prevState) => ({
      ...prevState,
      email: { ...registrationData.email, required: !isPhoneAccount },
      phone: { ...registrationData.phone, required: isPhoneAccount },
    }));

    props.onSetIsPhoneAccount(isPhoneAccount);
  };

  let content = null;
  let formValid = Object.entries(registrationData).every((d) =>
    dataValid(d[1])(false),
  );

  if (!registered) {
    content = (
      <RegistrationForm
        data={registrationData}
        email={props.email}
        error={stateError}
        isPhoneAccount={props.isPhoneAccount}
        isValid={dataValid(true)}
        loading={loading}
        phone={props.phone}
        onBlurTaxNumber={handleTaxNumberOnBlur}
        onBlurInput={saveActivity}
        onBlurPhonenumber={handlePhoneValidationBlur}
        onChangeData={handleChangeData}
        onChangeBusinessId={handleChangeBusinessId}
        onChangeEmail={props.onSetEmail}
        onChangePhone={props.onSetPhone}
        passwordHelper={passwordHelper(props.t)}
        password2Helper={password2Helper(props.t)}
        register={register}
        submitEnabled={formValid}
        togglePhoneAccount={togglePhoneAccount}
        foundByBusinessId={foundByBusinessId}
        validCompany={validCompany}
        companyData={companyData}
        handleDateOfBirthYearOnBlur={handleDateOfBirthYearOnBlur}
        handleBusinessIdCountryRadioChange={handleBusinessIdCountryRadioChange}
        businessIdCountry={businessIdCountry}
        taxNumberLoading={taxNumberLoading}
        handleSearchTermChange={handleSearchTermChange}
        handleCompanySelect={handleCompanySelect}
        companySearchValue={companySearchValue}
        companySearchOptions={companySearchOptions}
        companySearchLoading={companySearchLoading}
        isEmployerLimitedCompany={isEmployerLimitedCompany}
      />
    );
  } else if (userConfirmed) {
    content = <h3>Registration successful, {cognitoUser.getUsername()}</h3>;
  } else {
    props.setVerificationView({
      projectKey: registrationData.projectKey.value,
      activityId: activityId,
    });
  }

  return content;
}

RegistrationLogic.propTypes = {
  t: PropTypes.func.isRequired,
  setVerificationView: PropTypes.func.isRequired,
  onSetEmail: PropTypes.func.isRequired,
  onSetPhone: PropTypes.func.isRequired,
  onSetIsPhoneAccount: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  phone: PropTypes.string.isRequired,
  isPhoneAccount: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  return {
    setVerificationView: PropTypes.func.isRequired,
    email: state.user.email,
    phone: state.user.phone,
    isPhoneAccount: state.user.isPhoneAccount,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setVerificationView: (data) => dispatch(setRoute(ROUTE.VERIFICATION, data)),
    onSetEmail: (email) => dispatch(setEmail(email)),
    onSetPhone: (phone) => dispatch(setPhone(phone)),
    onSetIsPhoneAccount: (isPhoneAccount) =>
      dispatch(setIsPhoneAccount(isPhoneAccount)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(RegistrationLogic));
export { RegistrationLogic };
